<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { ref } from 'vue';

  import type { EmailSender } from '@stuut/api/models';
  import { indexOf } from '@stuut/common/src/utils';
  import { Dialog, DialogContent } from '@ui/components/dialog';
  import { toast } from '@ui/components/sonner';

  import LinkEmailForm from '@/components/email-senders/LinkEmailForm.vue';
  import { useEmailSenders, useProfile } from '@/store';

  const emits = defineEmits<{
    (e: 'emailLinkComplete', emailSender?: EmailSender): void;
  }>();
  const profileStore = useProfile();
  const emailSenderStore = useEmailSenders();

  const showDialog = ref(false);
  const linkedEmailSender = ref<EmailSender | undefined>();
  const { emailSenders } = storeToRefs(emailSenderStore);

  const open = (emailSender?: EmailSender) => {
    showDialog.value = true;
    linkedEmailSender.value = emailSender;
  };

  const close = () => {
    showDialog.value = false;
    linkedEmailSender.value = undefined;
  };

  const onEmailLinked = async (emailSender: EmailSender) => {
    showDialog.value = false;

    if (!emailSenders.value?.length) await setupEmailSenders();

    const existingSenderIdx = indexOf(
      emailSenders.value,
      (es: EmailSender) => es.id === emailSender.id,
    );

    if (existingSenderIdx === -1) {
      emailSenders.value.push(emailSender);
    } else {
      emailSenders.value[existingSenderIdx] = emailSender;
    }

    if (!profileStore.profile.default_email_sender_id) {
      profileStore.profile.default_email_sender_id = emailSender.id;
    }

    emits('emailLinkComplete', emailSender);
  };

  const setupEmailSenders = async () => {
    try {
      return emailSenderStore.fetchEmailSenders();
    } catch (err: any) {
      toast.error(err.clientMessage);
    }
  };

  defineExpose({
    close,
    open,
    showDialog,
  });
</script>

<template>
  <Dialog v-model:open="showDialog">
    <DialogContent class="w-[390px] h-[400px]" :disable-dismiss-on="['esc', 'interact-outside']">
      <LinkEmailForm
        :existing-email-sender="linkedEmailSender"
        @success="onEmailLinked"
        @close="close"
      />
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
